import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTED = [
  "How do I prep for the RPR exam?",
  "Best AI tool for drafting NDAs?",
  "Do you serve Washington DC?",
];

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi — I'm the LexWorkspace assistant. Ask me anything about stenography speed training or AI legal drafting and I'll point you to the right platform.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        message,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I had a problem connecting. Please try again in a moment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 left-6 h-14 w-14 rounded-full bg-black text-white shadow-2xl hover:scale-105 transition-transform flex items-center justify-center z-50"
        aria-label="Open assistant"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            data-testid="ai-chat-window"
            className="fixed bottom-24 left-6 w-[calc(100vw-3rem)] sm:w-96 bg-white/95 backdrop-blur-2xl border border-black/10 shadow-[0_8px_32px_rgba(0,0,0,0.18)] rounded-2xl overflow-hidden z-50 flex flex-col"
            style={{ maxHeight: "70vh" }}
          >
            <div className="p-4 border-b border-black/10 bg-white">
              <p className="font-serif-display text-xl leading-none">
                LexWorkspace Assistant
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Steno training & AI legal drafting — ask anything.
              </p>
            </div>

            <div
              ref={scrollRef}
              className="chat-scroll flex-1 overflow-y-auto p-4 space-y-3"
              style={{ minHeight: 240 }}
            >
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={
                    m.role === "user"
                      ? "ml-auto bg-black text-white px-4 py-2 rounded-lg max-w-[85%] text-sm"
                      : "mr-auto bg-gray-100 text-[#111] px-4 py-2 rounded-lg max-w-[90%] text-sm whitespace-pre-wrap"
                  }
                  data-testid={`ai-chat-message-${m.role}-${idx}`}
                >
                  {m.content}
                </div>
              ))}
              {loading && (
                <div className="mr-auto bg-gray-100 px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              )}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-3 flex flex-wrap gap-2">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    data-testid={`ai-chat-suggested-${s.slice(0, 12)}`}
                    onClick={() => send(s)}
                    className="text-xs border border-black/20 px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-black/10 p-3 flex items-center gap-2 bg-white"
            >
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                className="flex-1 text-sm border-b border-black/20 bg-transparent rounded-none px-0 py-2 focus:border-black focus:outline-none"
                disabled={loading}
              />
              <button
                type="submit"
                data-testid="ai-chat-send"
                disabled={loading || !input.trim()}
                className="h-9 w-9 bg-black text-white flex items-center justify-center disabled:opacity-30"
                aria-label="Send"
              >
                <Send size={14} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
